import { IconButton, Stack, TableCell, TableRow } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '../../selectField';

type Props = {
  entity: any;
  eventSubscription?: any;
  eventSources: any[];
  errror?: string;
  onAdd: (eventSubscription: any) => void;
  onEdit: (oldSubscription: any, newSubscription: any) => void;
  onDelete: (eventSubscription: any) => void;
  onCancelAdd: () => void;
};

const EventSubscription = ({
  entity,
  eventSubscription,
  eventSources = [],
  errror,
  onAdd,
  onEdit,
  onDelete,
  onCancelAdd,
}: Props) => {
  const { t } = useTranslation();

  const [eventSourceId, setEventSourceId] = useState<any>(eventSubscription?.eventSourceId);
  const [eventName, setEventName] = useState<string>(eventSubscription?.eventName);

  const [eventNames, setEventNames] = useState<string[]>([]);

  const [changed, setChanged] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    const es = eventSources.find((es) => es.entityId === eventSourceId);
    if (es) {
      setEventNames((es.eventDefs || []).map((ed: any) => ({ label: ed.eventLabel, value: ed.eventName })));
    } else {
      setEventNames([]);
    }
  }, [eventSources, eventSourceId]);

  const handleEventSourceChange = (eventSourceId: string) => {
    setEventSourceId(eventSourceId);
    setChanged(true);
  };

  const handleEventNameChange = (eventName: string) => {
    setEventName(eventName);
    setChanged(true);
  };

  const editEvent = () => {
    setEditMode(true);
  };

  const cancelAdd = () => {
    onCancelAdd();
  };

  const cancelEdit = () => {
    setEditMode(false);
  };

  const addSubscription = () => {
    let hasError = false;
    const errors: any = {};
    if (!eventSourceId) {
      errors.eventSourceId = t('common.errors.requiredField');
      hasError = true;
    }

    if (!eventName) {
      errors.eventName = t('common.errors.requiredField');
      hasError = true;
    }
    setErrors(errors);
    if (!hasError) {
      const es = eventSources.find((es) => es.entityId === eventSourceId);
      onAdd({
        eventSourceId: eventSourceId,
        eventSourceType: es.entityType,
        eventSourceDefId: es.entityDefId,
        eventSubscriberId: entity.id || entity.nodeId,
        eventSubscriberType: entity.entityType,
        eventSubscriberDefId: entity.entityDefId,
        eventName,
      });
    }
  };

  const editSubscription = () => {
    let hasError = false;
    const errors: any = {};
    if (!eventSourceId) {
      errors.eventSourceId = t('common.errors.requiredField');
      hasError = true;
    }

    if (!eventName) {
      errors.eventName = t('common.errors.requiredField');
      hasError = true;
    }
    setErrors(errors);
    if (!hasError) {
      const es = eventSources.find((es) => es.entityId === eventSourceId);
      onEdit(eventSubscription, {
        eventSourceId: eventSourceId,
        eventSourceType: es.entityType,
        eventSourceDefId: es.entityDefId,
        eventSubscriberId: entity.id || entity.nodeId,
        eventSubscriberType: entity.entityType,
        eventSubscriberDefId: entity.entityDefId,
        eventName,
      });
      setEditMode(false);
    }
  };

  const deleteSubscription = () => {
    const es = eventSources.find((es) => es.entityId === eventSourceId);
    onDelete({
      eventSourceId: eventSourceId,
      eventSourceType: es.entityType,
      eventSourceDefId: es.entityDefId,
      eventSubscriberId: entity.id || entity.nodeId,
      eventSubscriberType: entity.entityType,
      eventSubscriberDefId: entity.entityDefId,
      eventName,
    });
  };

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell sx={{ width: '40%', maxWidth: '40%', overflow: 'hidden', py: 1 }}>
        <SelectField
          required
          options={eventSources.map((es) => {
            return {
              value: es.entityId,
              label: `${es.entityName || `${t(`common.entityType.${es.entityType}`)}`} (${es.entityDefName})`,
            };
          })}
          value={eventSourceId}
          autoFocus
          onChange={handleEventSourceChange}
          error={errors.eventSourceId}
          disabled={!(!eventSubscription || editMode)}
          fullWidth={true}
        />
      </TableCell>
      <TableCell sx={{ width: '40%', maxWidth: '40%', overflow: 'hidden', py: 1 }}>
        <SelectField
          required
          options={eventNames}
          value={eventName}
          autoFocus
          onChange={handleEventNameChange}
          error={errors.eventName}
          disabled={!(!eventSubscription || editMode)}
        />
      </TableCell>
      {!eventSubscription && (
        <TableCell align="left" sx={{ width: '20%', py: 1 }}>
          <Stack direction={'row'} alignItems={'center'}>
            <IconButton size="small" onClick={addSubscription} color="primary" disabled={!changed}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => cancelAdd()}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
      )}
      {eventSubscription && editMode && (
        <TableCell align="left" sx={{ width: '20%', py: 1 }}>
          <Stack direction={'row'} alignItems={'center'}>
            <IconButton size="small" onClick={editSubscription} color="primary" disabled={!changed}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => cancelEdit()}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
      )}
      {eventSubscription && !editMode && (
        <TableCell align="left" sx={{ width: '20%', py: 1 }}>
          <Stack direction={'row'} alignItems={'center'}>
            <IconButton size="small" onClick={() => editEvent()} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => deleteSubscription()} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
};

export default EventSubscription;
